import { useState, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Loader2, Check, Plus, Pencil, Archive, Search, Upload, Image as ImageIcon,
  FileJson, FileUp, Eye, CloudUpload, CheckCircle2, XCircle, MinusCircle,
} from "lucide-react";
import type { UgcExpressStyleRecord } from "@shared/schema";
import { UGC_EXPRESS_DEFAULT_STYLE_VALUE } from "@shared/schema";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

// ── UGC Express Style manager (Admin → Content & Brands) ────────────────────
// Global style library manager. The list mirrors the Slideshow Express style
// manager layout exactly (search + Bulk Import + New style header, card rows
// with picker photo / name / slug / short description and edit + archive
// actions). Edit/create happens in a dialog mirroring the Slideshow Express
// "Edit style" layout (Label, Picker photo, Short description, Style
// description) — deliberately WITHOUT a slug field (the stable value is
// auto-derived server-side) and WITHOUT overlay-text settings. The picture is
// display-only (dropdown thumbnail), never a generation ref. The default
// Hyperrealistic UGC style can be edited but not deleted.

type ImportEntryResult = {
  index: number;
  value: string | null;
  label: string | null;
  status: "created" | "skipped" | "error";
  reason?: string;
};
type ImportResponse = {
  created: number;
  skipped: number;
  errored: number;
  results: ImportEntryResult[];
};

const IMPORT_EXAMPLE = `[
  {
    "name": "Claymation",
    "shortDescription": "Playful stop-motion clay look",
    "description": "Describe the whole style here...",
    "imageUrl": "https://example.com/photo.webp",
    "sortOrder": 10
  },
  {
    "label": "Candid Friend-Cam",
    "short_description": "snake_case keys work too",
    "style_description": "Describe the whole style here...",
    "picker_photo_url": "data:image/png;base64,iVBORw0KG..."
  }
]`;

export default function UgcExpressStyleManager() {
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<UgcExpressStyleRecord | null>(null);
  const [name, setName] = useState("");
  const [shortDescription, setShortDescription] = useState("");
  const [description, setDescription] = useState("");
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState<UgcExpressStyleRecord | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Bulk JSON import dialog state
  const [importOpen, setImportOpen] = useState(false);
  const [importEntries, setImportEntries] = useState<unknown[] | null>(null);
  const [importFileName, setImportFileName] = useState<string>("");
  const [importFileSize, setImportFileSize] = useState<number>(0);
  const [importParseError, setImportParseError] = useState<string>("");
  const [importResult, setImportResult] = useState<ImportResponse | null>(null);
  const [importWasDryRun, setImportWasDryRun] = useState(false);
  const importInputRef = useRef<HTMLInputElement>(null);

  // Global query defaults (retry:false + staleTime:Infinity) would cache a
  // transient first-paint 401 forever and wedge this shared endpoint empty, so
  // override them: retry the auth race and re-fetch on mount.
  const { data: styles = [], isLoading } = useQuery<UgcExpressStyleRecord[]>({
    queryKey: ["/api/ugc-express-styles"],
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    staleTime: 30_000,
    refetchOnMount: true,
  });

  const isDefault = editing?.value === UGC_EXPRESS_DEFAULT_STYLE_VALUE;

  const openCreate = () => {
    setEditing(null);
    setName("");
    setShortDescription("");
    setDescription("");
    setImageUrl(null);
    setDialogOpen(true);
  };
  const openEdit = (s: UgcExpressStyleRecord) => {
    setEditing(s);
    setName(s.name ?? "");
    setShortDescription(s.shortDescription ?? "");
    setDescription(s.description ?? "");
    setImageUrl(s.imageUrl ?? null);
    setDialogOpen(true);
  };

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["/api/ugc-express-styles"] });

  const saveMut = useMutation({
    mutationFn: async () => {
      const body = {
        name: name.trim(),
        shortDescription: shortDescription.trim() || null,
        description: description.trim() || null,
        imageUrl,
      };
      if (!editing) {
        const res = await apiRequest("POST", "/api/ugc-express-styles", body);
        return res.json();
      }
      const res = await apiRequest("PATCH", `/api/ugc-express-styles/${editing.id}`, body);
      return res.json();
    },
    onSuccess: () => {
      invalidate();
      setDialogOpen(false);
      toast({ title: "Style saved" });
    },
    onError: (e: any) => toast({ title: "Could not save style", description: e?.message, variant: "destructive" }),
  });

  const archiveMut = useMutation({
    mutationFn: async (id: string) => apiRequest("POST", `/api/ugc-express-styles/${id}/archive`),
    onSuccess: () => {
      invalidate();
      setArchiveTarget(null);
      setDialogOpen(false);
      toast({ title: "Style deleted" });
    },
    onError: (e: any) => toast({ title: "Could not delete style", description: e?.message, variant: "destructive" }),
  });

  const onPickFile = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch("/api/ugc-express-styles/upload-image", { method: "POST", body: fd, credentials: "include" });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error((j as any)?.message || "Upload failed");
      }
      const j = (await res.json()) as { url: string };
      setImageUrl(j.url);
    } catch (e: any) {
      toast({ title: "Image upload failed", description: e?.message, variant: "destructive" });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleImportFile = (file: File) => {
    setImportResult(null);
    setImportParseError("");
    setImportEntries(null);
    setImportFileName(file.name);
    setImportFileSize(file.size);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result));
        const entries = Array.isArray(parsed) ? parsed : parsed?.entries;
        if (!Array.isArray(entries) || entries.length === 0) {
          setImportParseError("The file must contain a JSON array of style objects (or { \"entries\": [...] }).");
          return;
        }
        setImportEntries(entries);
      } catch (e: any) {
        setImportParseError(`Couldn't parse this file as JSON: ${e.message}`);
      }
    };
    reader.onerror = () => setImportParseError("Couldn't read the file.");
    reader.readAsText(file);
  };

  const importMutation = useMutation({
    mutationFn: async (dryRun: boolean) => {
      const res = await apiRequest("POST", "/api/ugc-express-styles/import", { entries: importEntries, dryRun });
      const data = (await res.json()) as ImportResponse;
      return { data, dryRun };
    },
    onSuccess: ({ data, dryRun }) => {
      setImportResult(data);
      setImportWasDryRun(dryRun);
      if (!dryRun) {
        setImportEntries(null);
        invalidate();
      }
      toast({
        title: dryRun ? "Dry run complete" : "Import complete",
        description: `${data.created} ${dryRun ? "would be created" : "created"}, ${data.skipped} skipped, ${data.errored} failed.`,
        variant: data.errored > 0 && data.created === 0 ? "destructive" : undefined,
      });
    },
    onError: (e: Error) => toast({ title: "Bulk import failed", description: e.message, variant: "destructive" }),
  });

  const openImport = () => {
    setImportEntries(null);
    setImportFileName("");
    setImportFileSize(0);
    setImportParseError("");
    setImportResult(null);
    setImportWasDryRun(false);
    setImportOpen(true);
  };

  const filtered = (styles ?? []).filter((s) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (s.name ?? "").toLowerCase().includes(q) ||
      (s.value ?? "").toLowerCase().includes(q) ||
      (s.shortDescription ?? "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-4" data-testid="section-ugc-express-styles">
      <p className="text-sm text-muted-foreground">
        The global bank of video styles for UGC Express. Edits appear instantly in the picker;
        runs that already chose a style are unaffected.
      </p>

      <div className="flex items-center justify-between gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search styles..." className="pl-9 h-9" data-testid="input-search-ugc-styles" />
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={openImport} size="sm" variant="outline" data-testid="button-import-ugc-styles">
            <FileUp className="h-4 w-4 mr-2" /> Bulk Import
          </Button>
          <Button onClick={openCreate} size="sm" data-testid="button-new-ugc-style">
            <Plus className="h-4 w-4 mr-2" /> New style
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading...
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground" data-testid="text-ugc-styles-empty">
          No styles yet. Create one to get started.
        </div>
      ) : (
        <div className="grid gap-2.5">
          {filtered.map((s) => (
            <Card key={s.id} data-testid={`card-manage-ugc-style-${s.id}`}>
              <CardContent className="flex items-start justify-between gap-3 p-3">
                <div className="flex items-start gap-3 min-w-0">
                  {s.imageUrl ? (
                    <img src={s.imageUrl} alt="" className="h-12 w-12 rounded-lg object-cover border border-border/50 shrink-0" data-testid={`img-manage-ugc-style-${s.id}`} />
                  ) : (
                    <div className="h-12 w-12 rounded-lg bg-muted/50 border border-border/40 flex items-center justify-center shrink-0">
                      <ImageIcon className="h-5 w-5 text-muted-foreground" />
                    </div>
                  )}
                  <div className="space-y-0.5 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold leading-tight" data-testid={`text-manage-ugc-style-name-${s.id}`}>{s.name}</p>
                      {s.value === UGC_EXPRESS_DEFAULT_STYLE_VALUE && (
                        <Badge variant="secondary" className="text-[10px]">Default</Badge>
                      )}
                    </div>
                    <p className="text-[11px] text-muted-foreground font-mono">{s.value}</p>
                    {s.shortDescription && (
                      <p className="text-xs text-muted-foreground line-clamp-2">{s.shortDescription}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(s)}
                    data-testid={`button-edit-ugc-style-${s.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  {s.value !== UGC_EXPRESS_DEFAULT_STYLE_VALUE && (
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setArchiveTarget(s)}
                      data-testid={`button-delete-ugc-style-${s.id}`}>
                      <Archive className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create / Edit dialog — mirrors the Slideshow Express edit-style layout
          (no slug field, no slideshow-text section). */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit style" : "New style"}</DialogTitle>
            <DialogDescription>
              This description steers every Sim agent for this style. Edits apply to new runs only —
              runs already in flight keep the style they were submitted with.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="ugc-style-label">Label</Label>
              <Input
                id="ugc-style-label"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Claymation"
                data-testid="input-style-name"
              />
            </div>

            <div className="space-y-1.5">
              <Label>Picker photo (500×500)</Label>
              <p className="text-xs text-muted-foreground">Shown on this style's card in the picker. Any image works — it's cropped to a centered 500×500 square.</p>
              <div className="flex items-center gap-3">
                <div className="h-20 w-20 rounded-lg border border-border/60 bg-muted/40 overflow-hidden shrink-0 flex items-center justify-center">
                  {imageUrl ? (
                    <img src={imageUrl} alt="" className="h-full w-full object-cover" data-testid="img-style-picture" />
                  ) : (
                    <ImageIcon className="h-6 w-6 text-muted-foreground" />
                  )}
                </div>
                <div className="flex flex-col items-start gap-1.5">
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => { onPickFile(e.target.files?.[0]); }}
                    data-testid="input-style-photo"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={uploading}
                    onClick={() => fileRef.current?.click()}
                    data-testid="button-upload-style-image"
                  >
                    {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                    {imageUrl ? "Replace photo" : "Upload photo"}
                  </Button>
                  {imageUrl && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 text-muted-foreground"
                      onClick={() => setImageUrl(null)}
                      data-testid="button-remove-style-image"
                    >
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ugc-style-short-description">Short description / subtitle</Label>
              <Input
                id="ugc-style-short-description"
                value={shortDescription}
                onChange={(e) => setShortDescription(e.target.value)}
                placeholder="A relatable, phone-shot creator vibe"
                data-testid="input-style-short-description"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="ugc-style-description">Style description</Label>
              <p className="text-xs text-muted-foreground">
                The single free-text message that steers every Sim agent for this style. Describe the
                whole style in one place — what it is, its emotional arc, how it works with or without
                a product, how the imagery should look, and what to avoid.
              </p>
              <Textarea
                id="ugc-style-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={12}
                className="font-normal"
                data-testid="input-style-description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="button-cancel-style">
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => saveMut.mutate()}
              disabled={!name.trim() || saveMut.isPending || uploading}
              data-testid="button-save-style"
            >
              {saveMut.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
              {editing ? "Save changes" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk JSON import dialog — mirrors the slideshow-style bulk importer. */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileUp className="h-5 w-5" />
              Bulk Import UGC Styles
            </DialogTitle>
            <DialogDescription>
              Upload a master JSON file (one entry per style). Existing rows are matched by{" "}
              <code>value</code> slug and skipped; new slugs are created. Use Dry Run to preview
              what would happen without changing anything.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="ugc-import-metadata">
                <span className="inline-flex items-center gap-1.5">
                  <FileJson className="h-4 w-4" /> Metadata JSON
                </span>
              </Label>
              <Input
                id="ugc-import-metadata"
                ref={importInputRef}
                type="file"
                accept="application/json,.json"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportFile(f); }}
                data-testid="input-import-file"
              />
              <p className="text-xs text-muted-foreground">
                Either an array <code>[...]</code> or an object <code>{`{ "entries": [...] }`}</code>.{" "}
                <span className="font-medium text-foreground">name</span> (or <code>label</code>) is required per entry; optional:{" "}
                <code>value</code>, <code>shortDescription</code>, <code>description</code>,{" "}
                <code>imageUrl</code>, <code>sortOrder</code>. Snake_case keys (<code>short_description</code>,{" "}
                <code>style_description</code>, <code>picker_photo_url</code>, <code>image_url</code>,{" "}
                <code>sort_order</code>) work too. The photo can be a hosted URL or a base64{" "}
                <code>data:image/...</code> string — base64 photos are stored durably at 500×500.
              </p>
              {importFileName && (
                <p className="text-xs" data-testid="text-import-filename">
                  {importFileName} · {(importFileSize / 1024).toFixed(1)} KB
                </p>
              )}
            </div>

            <details className="rounded-md border bg-muted/30 p-3">
              <summary className="text-xs font-medium cursor-pointer">Example file</summary>
              <pre className="text-[11px] font-mono bg-background/80 rounded p-2 mt-2 overflow-x-auto" data-testid="text-import-example">{IMPORT_EXAMPLE}</pre>
            </details>

            {importParseError && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive" data-testid="text-import-parse-error">
                {importParseError}
              </div>
            )}

            {importEntries && !importResult && (
              <div className="rounded-md border p-3 text-sm" data-testid="text-import-preview">
                Ready to import <span className="font-semibold">{importEntries.length}</span>{" "}
                style entr{importEntries.length === 1 ? "y" : "ies"}.
              </div>
            )}

            {importResult && (
              <div className="space-y-2" data-testid="section-import-results">
                {importWasDryRun && (
                  <p className="text-xs text-muted-foreground" data-testid="text-import-dryrun-note">
                    Dry run — nothing was saved. Click Import to apply.
                  </p>
                )}
                <div className="flex items-center gap-4 text-sm">
                  <span className="flex items-center gap-1.5 text-green-600 dark:text-green-400" data-testid="text-import-created">
                    <CheckCircle2 className="h-4 w-4" /> {importResult.created} {importWasDryRun ? "would be created" : "created"}
                  </span>
                  <span className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400" data-testid="text-import-skipped">
                    <MinusCircle className="h-4 w-4" /> {importResult.skipped} skipped
                  </span>
                  <span className="flex items-center gap-1.5 text-red-600 dark:text-red-400" data-testid="text-import-errored">
                    <XCircle className="h-4 w-4" /> {importResult.errored} failed
                  </span>
                </div>
                <div className="rounded-md border divide-y max-h-64 overflow-y-auto">
                  {importResult.results.map((r) => (
                    <div key={r.index} className="flex items-start gap-2 p-2 text-xs" data-testid={`row-import-result-${r.index}`}>
                      {r.status === "created" ? (
                        <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 shrink-0 text-green-600 dark:text-green-400" />
                      ) : r.status === "skipped" ? (
                        <MinusCircle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
                      ) : (
                        <XCircle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-red-600 dark:text-red-400" />
                      )}
                      <div className="min-w-0">
                        <span className="font-medium">
                          {r.label || r.value || `Entry ${r.index + 1}`}
                        </span>
                        {r.value && <span className="text-muted-foreground font-mono ml-1.5">{r.value}</span>}
                        {r.reason && <div className="text-muted-foreground mt-0.5">{r.reason}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex-wrap gap-2 sm:justify-start">
            <Button
              variant="outline"
              disabled={!importEntries || importMutation.isPending}
              onClick={() => importMutation.mutate(true)}
              data-testid="button-import-dry-run"
            >
              {importMutation.isPending && importMutation.variables === true ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <Eye className="h-4 w-4 mr-1.5" />
              )}
              Dry Run
            </Button>
            <Button
              disabled={!importEntries || importMutation.isPending}
              onClick={() => importMutation.mutate(false)}
              data-testid="button-run-import"
            >
              {importMutation.isPending && importMutation.variables === false ? (
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              ) : (
                <CloudUpload className="h-4 w-4 mr-1.5" />
              )}
              Import
            </Button>
            <Button variant="ghost" onClick={() => setImportOpen(false)} data-testid="button-close-import">
              {importResult && !importWasDryRun ? "Close" : "Cancel"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!archiveTarget} onOpenChange={(o) => !o && setArchiveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete style?</AlertDialogTitle>
            <AlertDialogDescription>
              "{archiveTarget?.name}" will be removed from the picker. Runs that already chose it
              keep working — they still resolve to coherent guidance.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => archiveTarget && archiveMut.mutate(archiveTarget.id)}
              data-testid="button-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
